<script setup>
import { onMounted, watch, ref, computed, markRaw } from 'vue';
import { useRoute, useHead } from '#app';
import { useWpPage } from '@/composables/useWpPage';
import { usePageStore } from '@/stores/pageStore';
import { useMenuStore } from '@/stores/menuStore';
import { useAsyncData } from '#app';
import { storeToRefs } from 'pinia';

// Import all possible dynamic components
import HomeBannerHero from '@/components/heros/HomePageHero.vue';
import PhoneHeroBanner from '@/components/heros/PagePhoneHero.vue';
import LegalPageHero from '@/components/heros/LegalPageHero.vue';
import Hero from '@/components/heros/PageHero.vue';
import TexteSection from '@/components/contents/TextSection.vue';
import VideoTextSection from '@/components/contents/VideoTextSection.vue';
import TitleAndUpperTitle from '@/components/contents/TitleAndUpperTitle.vue';
import BookingGenerating from '@/components/contents/BookingGenerating.vue';
import ImageTextSection from '@/components/contents/ImageTextSection.vue';
import TitleAndText from '@/components/contents/TitleAndText.vue';
import Cards from '@/components/contents/Cards.vue';
import Title from '@/components/contents/Title.vue';
import Content from '@/components/contents/Content.vue';
import Button from '@/components/contents/Button.vue';
import FileDownloadButton from './contents/FileDownloadButton.vue';

const props = defineProps({
  pageId: {
    type: Number,
    required: false,
  },
  pageSlug: {
    type: String,
    required: false,
  },
});

const route = useRoute()
let HeroBannerRaw = markRaw(Hero)

const store = usePageStore();
const menuStore = useMenuStore();
menuStore.fetchMenus();
await store.fetchFooterPages();

const { fetchPageData } = useWpPage();

const { pageId, footerPages, pageHead , linkPdf} = storeToRefs(store);
const pageContent = computed(() => store.pageContent);


// Create a map of component names to imported components
const componentMap = ref({
  HeroBanner: HeroBannerRaw,
  TexteSection: markRaw(TexteSection),
  VideoTextSection: markRaw(VideoTextSection),
  TitleAndUpperTitle: markRaw(TitleAndUpperTitle),
  BookingGenerating: markRaw(BookingGenerating),
  ImageTextSection: markRaw(ImageTextSection),
  TitleAndText: markRaw(TitleAndText),
  Cards: markRaw(Cards),
  Title: markRaw(Title),
  Content: markRaw(Content),
  Button: markRaw(Button),
  FileDownloadButton: markRaw(FileDownloadButton)
});

const updateComponentToLoad = async (id) => {
  if ( props.pageSlug === 'home') {
    componentMap.value.HeroBanner = markRaw(HomeBannerHero)
  }else if ( props.pageSlug === 'logiciel-360') { // Page logiciel 360
    componentMap.value.HeroBanner = markRaw(PhoneHeroBanner)  
  } else if (footerPages.value.some(page => props.pageSlug === page.url.replace(/^\/|\/$/g, '')) ) {
    componentMap.value.HeroBanner = markRaw(LegalPageHero)
  }else{
    componentMap.value.HeroBanner = HeroBannerRaw
  }
}

const asyncKey = computed(() => `wp-page-${Number(props.pageId) || props.pageSlug}`)

const { data: fetchedPageContent, pending, error, refresh } = await useAsyncData(
  asyncKey.value,
  () => fetchPageData(Number(props.pageId) || props.pageSlug),
  {
    server: true,             // SSR
    immediate: true,          // lance dès le montage
    watch: [() => route.params.slug, () => props.pageId, () => props.pageSlug], // relance quand slug/props changent
    // staleTime: 0           // (optionnel) pas de mise en cache côté client
  }
);
const updateHead = (newHead) => {
  const metaTags = [];

    // Itérer sur les clés de l'objet Yoast pour construire les balises meta
    Object.keys(newHead).forEach(key => {
      const content = newHead[key];
      if (key === 'description' && content) {
        metaTags.push({ name: 'description', content });
      } else if (key.startsWith('og_')) {
        const property = key.replace(/_/g, ':');
        metaTags.push({ property, content });
      } else if (key.startsWith('twitter_')) {
        const name = key.replace(/_/g, ':');
        metaTags.push({ name, content });
      }
    });

    // Gérer le schema JSON-LD
    const scriptTags = [];
    if (newHead.schema) {
      scriptTags.push({
        type: 'application/ld+json',
        children: JSON.stringify(newHead.schema, null, 2)
      });
    }

    // Appeler useHead avec la structure correcte
    useHead({
      title: newHead.title,
      meta: metaTags,
      link: newHead.canonical ? [{ rel: 'canonical', href: newHead.canonical }] : [],
      script: scriptTags,
      htmlAttrs: {
        lang: newHead.lang || 'fr'
      }
    });
}

// Update Pinia store with fetched data
watch(fetchedPageContent, async (newValue) => {
  if (newValue) {
    store.setPageContent(newValue.content);
    store.setPageHead(newValue.header);
    
    await updateComponentToLoad(store.pageId); // Await the call    
  }
}, { immediate: true });

watch(pageHead, (newHead) => {
  if (newHead) {
    updateHead(newHead);
  }
}, { immediate: true });


// Watch for prop changes and refresh data
watch(() => [props.pageId, props.pageSlug], ([newPageId, newPageSlug], [oldPageId, oldPageSlug]) => {

  if (newPageId !== oldPageId || newPageSlug !== oldPageSlug) {
    refresh();
    
    // updateComponentToLoad(newPageId); // Pass pageId from store
  }
});

// Watch pageId to update component map
watch(pageId, () => {  
  // updateComponentToLoad(pageId.value); // Pass pageId.value from storeToRefs
});

const onAfterLeave = () => {
  window.scrollTo(0, 0);
};
</script>

<template>
     
        <div class="wp-page-container" :class="{ 
          'home' : Number(props.pageId) === 2 ,
          'legal-page': footerPages.some(page => Number(page.id) === Number(props.pageId))
        }">
        <Transition name="fade"
       mode="in-out"
          @after-leave="onAfterLeave"
      >
          <div v-if="pending" key="loading" class="loading">Chargement de la page...</div>
              <div v-else-if="error" key="error" class="error-message"> 
                 <h2>Erreur</h2>
                <p>{{ error }}</p>
              </div>
            <div v-else key="content" class="wp-page-content">
            
              <template v-if="Array.isArray(pageContent)">
                <template v-for="(content, key) in pageContent" :key="key">
                  <component
                    hydrate-on-idle
                    :is="componentMap[content.component]"
                    :class="content.component.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase() + '-' + key "
                    :reverse=" content.reverse || false"
                    v-bind="content.props"
                  />
                </template>
              </template>
          </div>


       </Transition>

  </div>
</template>

<style scoped lang="scss">
.wp-page-container {
    padding: 0 25px;
    margin: 0 auto;
    min-height: 100vh;
    overflow-x: clip;
}

.loading, .error-message {
    text-align: center;
    padding: 4rem;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
.page-content {
  /* Styles pour le contenu rendu depuis WordPress */
  line-height: 1.6;
}

#main-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: -1;
}

.fade-enter-active,
.fade-leave-active {
  transition: all .75s ease-out ;
}

.fade-enter-from,
.fade-leave-to {
  filter: blur(10px);
  opacity: 0;
}
</style>
